import React from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that handles Azure AD authentication
 * Automatically redirects unauthenticated users to Azure AD login
 * Shows loading states during authentication process
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): React.ReactElement {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [loginAttempted, setLoginAttempted] = React.useState(false);
  const [domainValidated, setDomainValidated] = React.useState(false);
  
  // Helper function to check if email domain is allowed
  const isAllowedDomain = (email: string): boolean => {
    const allowedDomains = ['horwathhtl.com', 'barretthtl.com', 'theyounges.com'];
    const emailDomain = email.split('@')[1]?.toLowerCase();
    return allowedDomains.includes(emailDomain);
  };

  React.useEffect(() => {
    // Check if instance is already initialized
    const checkInitialization = async () => {
      try {
        // If getAllAccounts() works, it's initialized
        instance.getAllAccounts();
        setIsInitialized(true);
      } catch {
        // Not initialized, wait for it
        await instance.initialize();
        setIsInitialized(true);
      }
    };
    
    checkInitialization();
  }, [instance]);

  // Navigate to login page when needed - this hook must be at top level
  React.useEffect(() => {
    if (isInitialized && 
        inProgress === InteractionStatus.None && 
        accounts.length === 0 && 
        !loginAttempted) {
      setLoginAttempted(true);
      // Navigate to login page instead of automatic redirect
      navigate('/login');
    }
  }, [isInitialized, inProgress, accounts.length, loginAttempted, navigate]);

  // Validate domain after successful authentication
  React.useEffect(() => {
    if (isInitialized && 
        inProgress === InteractionStatus.None && 
        accounts.length > 0 && 
        !domainValidated) {
      
      const userEmail = accounts[0].username;
      
      // Check if the user's domain is allowed
      if (!isAllowedDomain(userEmail)) {
        console.warn('Access denied: User domain not allowed');
        navigate('/access-denied');
        return;
      }
      
      setDomainValidated(true);
    }
  }, [isInitialized, inProgress, accounts, domainValidated, navigate]);

  // Wait for initialization
  if (!isInitialized) {
    // Waiting for MSAL initialization
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Initializing...
        </Typography>
      </Box>
    );
  }

  // Check if authentication is in progress
  if (inProgress !== InteractionStatus.None) {
    // Authentication in progress
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Authenticating...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your credentials
        </Typography>
      </Box>
    );
  }

  // Check if user is authenticated
  if (accounts.length === 0) {
    // No authenticated accounts found, will navigate to login page
    
    // Show loading while navigation is happening
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Check if we're validating domain
  if (!domainValidated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verifying permissions...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your access permissions
        </Typography>
      </Box>
    );
  }

  // User is authenticated and domain validated, render protected content
  return <>{children}</>;
}